import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AGG_OPTIONS } from "@/lib/widgetCatalog";
import { QUICK_CALCS } from "@/lib/quickCalcs";
import { aggsForType } from "@/components/bi/InspectorFieldMenu";
import { isNumericType, readFieldDrag } from "@/lib/fieldWells";
import { tableInstances, instancesToConfig, moveInstance, newCid, baseKeyOf } from "@/lib/tableColumns";
import { GripVertical, X, Copy, Plus, Sigma, Table2 } from "lucide-react";
import { cn } from "@/lib/utils";

// Item 24 — one ordered "Columns" well for the Data Table. Every row is an independent column
// instance: the same field may appear many times, each with its own aggregation, show-value-as,
// display name and formatting. Drag the handle to reorder; the table renders this exact order.
export function TableColumnsEditor({ ins }) {
  const { config, patchConfig, dims, measureFields, disp, fieldType, governed, govDims, govMeas } = ins;
  const [drag, setDrag] = useState(null);
  const [over, setOver] = useState(null);
  const instances = tableInstances(config);
  const write = (list) => patchConfig(instancesToConfig(list));

  const roleFor = (field) => (isNumericType(fieldType(field)) ? "meas" : "dim");
  const addField = (field) => {
    if (!field) return;
    const role = roleFor(field);
    write([...instances, { cid: newCid(), role, entry: role === "meas" ? { field, agg: "sum" } : { field, date_part: "none" } }]);
  };
  const patchAt = (i, patch) => write(instances.map((x, j) => (j === i ? { ...x, entry: { ...x.entry, ...patch } } : x)));
  const setRole = (i, role) => write(instances.map((x, j) => {
    if (j !== i) return x;
    const f = x.entry.field;
    return { ...x, role, entry: role === "meas" ? { field: f, agg: "sum" } : { field: f, date_part: "none" } };
  }));
  const duplicate = (i) => {
    const src = instances[i];
    const copy = { cid: newCid(), role: src.role, entry: { ...src.entry } };
    delete copy.entry.cid; delete copy.entry.alias; delete copy.entry.clone_of;
    write([...instances.slice(0, i + 1), copy, ...instances.slice(i + 1)]);
  };
  const remove = (i) => write(instances.filter((_, j) => j !== i));

  const onDrop = (e) => {
    const p = readFieldDrag(e);
    if (!p || !p.field) return;
    e.preventDefault();
    addField(p.field);
  };
  const allowDrop = (e) => { if (e.dataTransfer?.types?.length) e.preventDefault(); };

  const dupCount = {};
  instances.forEach((i) => { const k = baseKeyOf(i.entry, i.role) || ""; dupCount[k] = (dupCount[k] || 0) + 1; });

  return (
    <div data-testid="table-columns-editor" onDragOver={allowDrop} onDrop={onDrop} className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label className="text-xs text-[color:var(--ink-3)] flex items-center gap-1"><Table2 className="w-3.5 h-3.5" />Columns</Label>
        <span className="text-[10px] text-[color:var(--ink-3)]">{instances.length} column{instances.length === 1 ? "" : "s"}</span>
      </div>
      <div className="space-y-1.5">
        {instances.map((it, i) => {
          const e = it.entry;
          const isMeas = it.role === "meas";
          const key = baseKeyOf(e, it.role) || "";
          const isDupe = dupCount[key] > 1;
          const fieldOpts = isMeas ? measureFields : dims;
          const govOpts = isMeas ? govMeas : govDims;
          const govMode = governed && !!e.key;
          return (
            <div key={it.cid} data-testid={`tcol-row-${i}`} data-role={it.role}
              draggable onDragStart={() => setDrag(i)} onDragEnd={() => { setDrag(null); setOver(null); }}
              onDragOver={(ev) => { if (drag !== null) { ev.preventDefault(); ev.stopPropagation(); setOver(i); } }}
              onDrop={(ev) => { if (drag !== null) { ev.preventDefault(); ev.stopPropagation(); write(moveInstance(instances, drag, i)); setDrag(null); setOver(null); } }}
              className={cn("rounded-lg border bg-[color:var(--surface)] p-1.5 space-y-1",
                over === i && drag !== null && drag !== i ? "border-[color:var(--brand-ocean)]" : "border-[color:var(--glass-border)]",
                drag === i && "opacity-50")}>
              <div className="flex items-center gap-1">
                <span data-testid={`tcol-handle-${i}`} title="Drag to reorder" className="cursor-grab text-[color:var(--ink-3)] shrink-0"><GripVertical className="w-3.5 h-3.5" /></span>
                <button data-testid={`tcol-role-${i}`} title={isMeas ? "Value column — click to group by this field instead" : "Group-by column — click to aggregate it instead"}
                  onClick={() => setRole(i, isMeas ? "dim" : "meas")}
                  className={cn("h-7 px-1.5 rounded-md border text-[10px] font-semibold shrink-0",
                    isMeas ? "bg-[color:rgba(43,78,255,0.10)] text-[color:var(--brand-ocean)] border-[color:rgba(43,78,255,0.3)]"
                      : "bg-[color:var(--surface-2)] text-[color:var(--ink-3)] border-[color:var(--glass-border)]")}>
                  {isMeas ? <Sigma className="w-3 h-3" /> : "☷"}
                </button>
                {govMode ? (
                  <Select value={e.key} onValueChange={(v) => patchAt(i, { key: v, field: (govOpts.find((x) => x.key === v)?.field) || e.field })}>
                    <SelectTrigger data-testid={`tcol-key-${i}`} className="h-7 text-xs bg-[color:var(--surface-2)] flex-1 min-w-0"><SelectValue /></SelectTrigger>
                    <SelectContent>{govOpts.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <Select value={e.field || ""} onValueChange={(v) => patchAt(i, { field: v })}>
                    <SelectTrigger data-testid={`tcol-field-${i}`} className="h-7 text-xs bg-[color:var(--surface-2)] flex-1 min-w-0"><SelectValue placeholder="Field" /></SelectTrigger>
                    <SelectContent>
                      {fieldOpts.map((c) => <SelectItem key={c.name} value={c.name}>{disp(c.name)}</SelectItem>)}
                      {e.field && !fieldOpts.some((c) => c.name === e.field) && <SelectItem value={e.field}>{disp(e.field)}</SelectItem>}
                    </SelectContent>
                  </Select>
                )}
                {isMeas && !govMode && (
                  <Select value={e.agg || "sum"} onValueChange={(v) => patchAt(i, { agg: v })}>
                    <SelectTrigger data-testid={`tcol-agg-${i}`} className="h-7 w-[92px] text-xs bg-[color:var(--surface-2)] shrink-0"><SelectValue /></SelectTrigger>
                    <SelectContent>{aggsForType(fieldType(e.field), AGG_OPTIONS).map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}</SelectContent>
                  </Select>
                )}
                <button data-testid={`tcol-dup-${i}`} title="Add another column for this field" onClick={() => duplicate(i)}
                  className="p-1 rounded text-[color:var(--ink-3)] hover:text-[color:var(--brand-ocean)] shrink-0"><Copy className="w-3.5 h-3.5" /></button>
                <button data-testid={`tcol-remove-${i}`} title="Remove this column" onClick={() => remove(i)}
                  className="p-1 rounded text-[color:var(--ink-3)] hover:text-[color:var(--danger)] shrink-0"><X className="w-3.5 h-3.5" /></button>
              </div>
              <div className="flex items-center gap-1 pl-6">
                {isMeas && (
                  <Select value={e.quickCalc?.kind || "none"} onValueChange={(v) => patchAt(i, { quickCalc: v === "none" ? null : { kind: v, ...(v === "moving_avg" ? { window: 3 } : {}), ...(v === "rank" ? { direction: "desc" } : {}) } })}>
                    <SelectTrigger data-testid={`tcol-calc-${i}`} className="h-6 text-[11px] bg-[color:var(--surface-2)] flex-1 min-w-0"><SelectValue placeholder="Show value as…" /></SelectTrigger>
                    <SelectContent>{QUICK_CALCS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                  </Select>
                )}
                <Input data-testid={`tcol-label-${i}`} value={e.label || ""} onChange={(ev) => patchAt(i, { label: ev.target.value || null })}
                  placeholder={isMeas ? "Column name…" : "Column name…"} className="h-6 text-[11px] bg-[color:var(--surface-2)] flex-1 min-w-0" />
              </div>
              {isDupe && (
                <p data-testid={`tcol-dupe-note-${i}`} className="text-[10px] text-[color:var(--ink-3)] pl-6">Independent copy of “{disp(e.field) || key}” — its own aggregation, calculation and formatting.</p>
              )}
            </div>
          );
        })}
        {!instances.length && (
          <p data-testid="tcol-empty" className="text-[11px] text-[color:var(--ink-3)] bg-[color:rgba(43,78,255,0.06)] border border-[color:rgba(43,78,255,0.2)] rounded-lg p-2">
            No columns yet — add fields below (or drag them from the Data pane). Numeric fields become value columns, everything else groups the rows.
          </p>
        )}
      </div>
      <Select value="" onValueChange={addField}>
        <SelectTrigger data-testid="tcol-add" className="h-7 text-xs bg-[color:var(--surface)]">
          <span className="flex items-center gap-1 text-[color:var(--brand-ocean)]"><Plus className="w-3.5 h-3.5" />Add column</span>
        </SelectTrigger>
        <SelectContent>{measureFields.map((c) => <SelectItem key={c.name} value={c.name}>{disp(c.name)}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}
